import React, { useEffect, useState } from 'react';
import { billingCyclesAPI } from '../../services/api';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { CalendarBlank, Gear, Lightning, CheckCircle, Warning, CaretRight, Spinner, ArrowLeft, CheckSquare } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { format, parseISO } from 'date-fns';
import CycleItemDialog, { flagLabel } from './CycleItemDialog';

const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
const money = (n) => `$${(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtP = (s, e) => `${format(parseISO(s), 'EEE MMM d')} – ${format(parseISO(e), 'EEE MMM d, yyyy')}`;

const SettingsDialog = ({ open, onClose, settings, onSaved }) => {
  const [day, setDay] = useState(String(settings?.week_start_day ?? 0));
  const [auto, setAuto] = useState(settings?.auto_generate ?? true);
  const [autoEmail, setAutoEmail] = useState(settings?.auto_email_on_approve ?? true);
  useEffect(() => { if (settings) { setDay(String(settings.week_start_day)); setAuto(settings.auto_generate); setAutoEmail(settings.auto_email_on_approve ?? true); } }, [settings, open]);
  const save = async () => {
    try { const r = await billingCyclesAPI.updateSettings({ week_start_day: Number(day), auto_generate: auto, auto_email_on_approve: autoEmail }); onSaved(r.data); toast.success('Billing cycle saved'); onClose(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
  };
  const endDay = DAYS[(Number(day) + 6) % 7];
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-2xl max-w-md" data-testid="cycle-settings-dialog">
        <DialogHeader><DialogTitle className="font-heading">Billing & Payroll Week</DialogTitle><DialogDescription>Applies to {settings?.office_name}. Invoices, pay statements and the 40-hour overtime rule all follow this week.</DialogDescription></DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label>Week starts on</Label>
            <Select value={day} onValueChange={setDay}><SelectTrigger className="rounded-xl" data-testid="cycle-week-start-select"><SelectValue /></SelectTrigger>
              <SelectContent>{DAYS.map((d, i) => <SelectItem key={d} value={String(i)}>{d}</SelectItem>)}</SelectContent></Select>
            <p className="text-xs text-muted-foreground">{DAYS[Number(day)]} 12:00 AM → {endDay} 11:59 PM</p>
          </div>
          <div className="flex items-center justify-between rounded-xl border border-border p-3">
            <div><p className="text-sm font-medium">Auto-generate drafts</p><p className="text-xs text-muted-foreground">Every {DAYS[Number(day)]} at 12:15 AM, drafts for the week that just closed are created automatically.</p></div>
            <Switch checked={auto} onCheckedChange={setAuto} data-testid="cycle-auto-switch" />
          </div>
          <div className="flex items-center justify-between rounded-xl border border-border p-3">
            <div><p className="text-sm font-medium">Email invoices on approval</p><p className="text-xs text-muted-foreground">When you approve a weekly invoice it is emailed (with PDF) to the client's email on file.</p></div>
            <Switch checked={autoEmail} onCheckedChange={setAutoEmail} data-testid="cycle-auto-email-switch" />
          </div>
          <div className="flex justify-end gap-2"><Button variant="outline" className="rounded-xl" onClick={onClose}>Cancel</Button><Button className="rounded-xl" onClick={save} data-testid="cycle-settings-save">Save</Button></div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const RunItems = ({ kind, run, onBack }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detailId, setDetailId] = useState(null);
  const isInvoice = kind === 'invoice';
  const load = async () => {
    setLoading(true);
    try { setItems((await billingCyclesAPI.run(kind, run.period_start)).data); } catch { toast.error('Failed to load'); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [kind, run.period_start]); // eslint-disable-line react-hooks/exhaustive-deps
  const approveAll = async () => {
    const drafts = items.filter(i => i.status === 'draft').length;
    if (!drafts || !window.confirm(`Approve all ${drafts} draft ${isInvoice ? 'invoices' : 'pay statements'} for this week?`)) return;
    try { const r = await billingCyclesAPI.approveAll(kind, run.period_start); toast.success(`Approved ${r.data.approved}`); load(); } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  const approveOne = async (e, id) => {
    e.stopPropagation();
    try { await billingCyclesAPI.approve(id); toast.success('Approved'); load(); } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };
  const total = items.reduce((s, i) => s + (i.total_amount || 0), 0);
  const hours = items.reduce((s, i) => s + (i.total_hours || 0), 0);
  return (
    <div className="space-y-4" data-testid="run-items">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" className="rounded-xl gap-1" onClick={onBack} data-testid="run-back-btn"><ArrowLeft className="w-4 h-4" /> All weeks</Button>
          <div><h3 className="font-heading font-semibold">{fmtP(run.period_start, run.period_end)}</h3><p className="text-xs text-muted-foreground">{items.length} {isInvoice ? 'clients' : 'caregivers'} · {hours.toFixed(2)} hrs · {money(total)}</p></div>
        </div>
        <Button className="rounded-xl gap-1.5 bg-emerald-600 hover:bg-emerald-700" onClick={approveAll} disabled={!items.some(i => i.status === 'draft')} data-testid="run-approve-all"><CheckSquare weight="bold" className="w-4 h-4" /> Approve all drafts</Button>
      </div>
      {loading ? <div className="py-12 flex justify-center"><Spinner className="w-6 h-6 animate-spin" /></div> : (
        <Card className="rounded-2xl border border-border"><CardContent className="p-0 divide-y divide-border">
          {items.length === 0 && <p className="p-8 text-center text-sm text-muted-foreground">No {isInvoice ? 'clients' : 'caregivers'} had visits this week.</p>}
          {items.map(i => (
            <div key={i.id} role="button" tabIndex={0} onClick={() => setDetailId(i.id)} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setDetailId(i.id); } }} className="w-full text-left flex items-center gap-4 px-4 py-3 hover:bg-muted/40 transition-colors cursor-pointer focus:outline-none focus-visible:bg-muted/40" data-testid={`run-item-${i.id}`}>
              <div className="flex-1 min-w-0">
                <p className="font-medium truncate">{i.client_name || i.caregiver_name}{i.invoice_number ? <span className="text-xs text-muted-foreground ml-2">{i.invoice_number}</span> : null}</p>
                <p className="text-xs text-muted-foreground">{i.total_visits} visits · {i.total_hours} hrs{!isInvoice && i.overtime_hours ? ` · ${i.overtime_hours} OT` : ''}</p>
                {i.flags?.length > 0 && <p className="text-[11px] text-amber-700 mt-0.5 flex items-center gap-1"><Warning weight="fill" className="w-3 h-3" /> {i.flags.map(flagLabel).join(' · ')}</p>}
              </div>
              <div className="text-right"><p className="font-semibold tabular-nums">{money(i.total_amount)}</p>
                {i.needs_adjustment ? <Badge className="rounded-full bg-rose-100 text-rose-800 text-[10px]">Changed after approval</Badge>
                  : <Badge className={`rounded-full text-[10px] ${i.status === 'approved' ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-800'}`}>{i.status === 'approved' ? 'Approved' : 'Draft'}</Badge>}
              </div>
              {i.status === 'draft' && <Button size="sm" variant="outline" className="rounded-xl gap-1 shrink-0 hidden sm:inline-flex" onClick={(e) => approveOne(e, i.id)} data-testid={`approve-item-${i.id}`}><CheckCircle className="w-4 h-4" /> Approve</Button>}
              <CaretRight className="w-4 h-4 text-muted-foreground shrink-0" />
            </div>
          ))}
        </CardContent></Card>
      )}
      <CycleItemDialog itemId={detailId} kind={kind} open={!!detailId} onClose={() => setDetailId(null)} onChanged={load} />
    </div>
  );
};

const BillingRunsTab = ({ kind }) => {
  const [settings, setSettings] = useState(null);
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [generating, setGenerating] = useState(false);
  const isInvoice = kind === 'invoice';

  const load = async () => {
    setLoading(true);
    try {
      const [s, r] = await Promise.all([billingCyclesAPI.settings(), billingCyclesAPI.runs(kind)]);
      setSettings(s.data); setRuns(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to load billing cycles'); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [kind]); // eslint-disable-line react-hooks/exhaustive-deps

  const generate = async (period) => {
    setGenerating(true);
    try {
      const r = await billingCyclesAPI.generate(period.start, kind);
      const n = r.data[kind]?.created ?? 0;
      toast.success(n ? `Created ${n} draft ${isInvoice ? 'invoice' : 'pay statement'}${n === 1 ? '' : 's'}` : 'Drafts already exist — opened for review');
      await load();
      setSelected({ period_start: r.data.period_start, period_end: r.data.period_end });
    } catch (e) { toast.error(e.response?.data?.detail || 'Generate failed'); } finally { setGenerating(false); }
  };

  if (loading && !settings) return <div className="py-12 flex justify-center"><Spinner className="w-6 h-6 animate-spin" /></div>;

  return (
    <div className="space-y-6" data-testid={`${kind}-runs-tab`}>
      <Card className="rounded-2xl border border-border bg-muted/20"><CardContent className="p-4 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-primary/10 text-primary flex items-center justify-center shrink-0"><CalendarBlank weight="duotone" className="w-5 h-5" /></div>
          <div>
            <p className="font-medium text-sm">Billing week: <span data-testid="cycle-week-label">{settings?.week_start_label} 12:00 AM → {settings?.week_end_label} 11:59 PM</span></p>
            <p className="text-xs text-muted-foreground">{settings?.auto_generate ? `Drafts auto-generate every ${settings?.week_start_label} at 12:15 AM.` : 'Auto-generate is off — use the buttons to create drafts.'} Drafts update live until approved.</p>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" className="rounded-xl gap-1.5" onClick={() => generate(settings.previous_period)} disabled={generating} data-testid="generate-previous-btn"><Lightning weight="fill" className="w-4 h-4" /> Last week</Button>
          <Button variant="outline" className="rounded-xl gap-1.5" onClick={() => generate(settings.current_period)} disabled={generating} data-testid="generate-current-btn"><Lightning className="w-4 h-4" /> Current week (preview)</Button>
          <Button variant="ghost" className="rounded-xl gap-1.5" onClick={() => setSettingsOpen(true)} data-testid="cycle-settings-btn"><Gear className="w-4 h-4" /> Settings</Button>
        </div>
      </CardContent></Card>

      {selected ? <RunItems kind={kind} run={selected} onBack={() => { setSelected(null); load(); }} /> : (
        <div className="space-y-3" data-testid="runs-list">
          {runs.length === 0 && <Card className="rounded-2xl"><CardContent className="p-10 text-center text-sm text-muted-foreground">No weekly runs yet. Click <strong>Last week</strong> to generate drafts for the most recent closed week.</CardContent></Card>}
          {runs.map(r => (
            <button key={r.period_start} onClick={() => setSelected(r)} className="w-full text-left rounded-2xl border border-border bg-white hover:border-primary/40 hover:shadow-sm transition-all p-4 flex items-center gap-4" data-testid={`run-${r.period_start}`}>
              <div className={`w-11 h-11 rounded-xl flex items-center justify-center shrink-0 ${r.status === 'approved' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>{r.status === 'approved' ? <CheckCircle weight="fill" className="w-5 h-5" /> : <CalendarBlank weight="duotone" className="w-5 h-5" />}</div>
              <div className="flex-1 min-w-0">
                <p className="font-medium">{fmtP(r.period_start, r.period_end)}</p>
                <p className="text-xs text-muted-foreground">{r.total_items} {isInvoice ? 'invoices' : 'pay statements'} · {r.approved} approved · {r.draft} draft{r.flagged ? ` · ${r.flagged} flagged` : ''}</p>
              </div>
              <div className="text-right">
                <p className="font-semibold tabular-nums">{money(r.total_amount)}</p>
                <p className="text-xs text-muted-foreground">{r.total_hours} hrs</p>
              </div>
              {r.needs_adjustment > 0 && <Badge className="rounded-full bg-rose-100 text-rose-800">{r.needs_adjustment} changed</Badge>}
              <Badge className={`rounded-full ${r.status === 'approved' ? 'bg-emerald-100 text-emerald-800' : r.status === 'in_review' ? 'bg-sky-100 text-sky-800' : 'bg-amber-100 text-amber-800'}`}>{r.status === 'approved' ? 'Approved' : r.status === 'in_review' ? 'Partially approved' : 'Draft'}</Badge>
              <CaretRight className="w-4 h-4 text-muted-foreground" />
            </button>
          ))}
        </div>
      )}

      <SettingsDialog open={settingsOpen} onClose={() => setSettingsOpen(false)} settings={settings} onSaved={(s) => { setSettings(s); load(); }} />
    </div>
  );
};

export default BillingRunsTab;
